import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { createClient } from '@supabase/supabase-js';
import './reset-password.css';

// Initialize Supabase client with validation
const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseKey = process.env.REACT_APP_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseKey) {
  console.error('Missing Supabase environment variables!');
}

const supabase = createClient(
  supabaseUrl || 'https://placeholder.supabase.co',
  supabaseKey || 'placeholder-key'
);

function ResetPassword() {
  const navigate = useNavigate();
  
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [alert, setAlert] = useState({ show: false, message: '', type: 'info' });
  const [showSuccess, setShowSuccess] = useState(false);
  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [isValidLink, setIsValidLink] = useState(false);
  const [sessionReady, setSessionReady] = useState(false);

  const showAlert = useCallback((message, type = 'info') => {
    setAlert({ show: true, message, type });
    if (type !== 'success') {
      setTimeout(() => {
        setAlert(prev => ({ ...prev, show: false }));
      }, 5000);
    }
  }, []);

  // Extract tokens from URL hash when component mounts
  useEffect(() => {
    const handlePasswordRecovery = async () => {
      // Check for env vars
      if (!supabaseUrl || !supabaseKey) {
        setErrorMessage('Server configuration error. Please contact support.');
        setShowError(true);
        return;
      }

      const hashParams = new URLSearchParams(window.location.hash.substring(1));
      const accessToken = hashParams.get('access_token');
      const refreshToken = hashParams.get('refresh_token');
      const type = hashParams.get('type');

      console.log('Token check:', { 
        hasAccessToken: !!accessToken, 
        hasRefreshToken: !!refreshToken, 
        type 
      });

      if (type === 'recovery' && accessToken) {
        try {
          const { data, error } = await supabase.auth.setSession({
            access_token: accessToken,
            refresh_token: refreshToken || '',
          });

          if (error) throw error;
          
          if (data.session) {
            setIsValidLink(true);
            setSessionReady(true);
            console.log('Session established for user:', data.session.user.email);
          } else {
            throw new Error('No session returned');
          }
        } catch (error) {
          console.error('Session error:', error);
          setErrorMessage(
            error.message?.includes('expired') 
              ? 'This reset link has expired. Please request a new one.'
              : 'Invalid reset link. Please request a new password reset email.'
          );
          setShowError(true);
        }
      } else {
        setErrorMessage('Invalid reset link. Missing required tokens.');
        setShowError(true);
      }
    };

    handlePasswordRecovery();
  }, [showAlert]);

  const validatePassword = useCallback(() => {
    const errors = [];
    
    if (newPassword.length < 6) {
      errors.push('Password must be at least 6 characters long');
    }

    if (/\s/.test(newPassword)) {
      errors.push('Password cannot contain spaces');
    }
    
    if (newPassword !== confirmPassword) {
      errors.push('Passwords do not match');
    }

    if (errors.length > 0) {
      showAlert(errors[0], 'error');
      return false;
    }
    
    return true;
  }, [newPassword, confirmPassword, showAlert]);

  const handleResetPassword = async (e) => {
    e.preventDefault();
    
    if (!validatePassword()) return;
    if (!sessionReady) {
      showAlert('Session not ready. Please refresh the page.', 'error');
      return;
    }

    setIsLoading(true);
    setAlert({ show: false, message: '', type: 'info' });

    try {
      // Verify we still have a session
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      
      if (sessionError || !session) {
        throw new Error('Session expired. Please request a new reset link.');
      }

      console.log('Updating password for user:', session.user.email);

      const { error } = await supabase.auth.updateUser({ password: newPassword });

      if (error) throw error;

      setShowSuccess(true);
      showAlert('Password reset successful!', 'success');
      
      await supabase.auth.signOut();
      
    } catch (error) {
      console.error('Password update error:', error);
      showAlert(
        error.message || 'Failed to reset password. Please try again.',
        'error'
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleResetPassword(e);
    }
  };

  if (showSuccess) {
    return (
      <div className="reset-password-page">
        <div className="container">
          <div className="logo-header">
            <div className="logo">Unlock</div>
            <div className="logo-subtitle">Reset Your Password</div>
          </div>
          <div className="reset-card">
            <div className="success-screen">
              <div className="success-icon"><i className="fas fa-check"></i></div>
              <h2 className="card-title">Password Reset Successful!</h2>
              <p className="card-subtitle">
                Your password has been updated. You can now log in with your new password.
              </p>
              <button className="return-btn" onClick={() => navigate('/')}>
                <i className="fas fa-sign-in-alt"></i> Return to Login
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (showError) {
    return (
      <div className="reset-password-page">
        <div className="container">
          <div className="logo-header">
            <div className="logo">Unlock</div>
            <div className="logo-subtitle">Reset Your Password</div>
          </div>
          <div className="reset-card">
            <div className="error-screen">
              <div className="error-icon"><i className="fas fa-exclamation"></i></div>
              <h2 className="card-title">Invalid Reset Link</h2>
              <p className="card-subtitle">{errorMessage}</p>
              <button className="return-btn" onClick={() => navigate('/')}>
                <i className="fas fa-home"></i> Return to Home
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!isValidLink) {
    return (
      <div className="reset-password-page">
        <div className="container">
          <div className="logo-header">
            <div className="logo">Unlock</div>
            <div className="logo-subtitle">Reset Your Password</div>
          </div>
          <div className="reset-card" style={{ textAlign: 'center', padding: '40px' }}>
            <div className="loading" style={{ margin: '0 auto 20px' }}></div>
            <p>Verifying reset link...</p>
            {!supabaseUrl && (
              <p style={{ color: 'red', marginTop: '20px' }}>
                Configuration error: Missing environment variables
              </p>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="reset-password-page">
      <div className="container">
        <div className="logo-header">
          <div className="logo">Unlock</div>
          <div className="logo-subtitle">Reset Your Password</div>
        </div>
        
        <div className="reset-card">
          {alert.show && alert.message && (
            <div className={`alert alert-${alert.type}`}>
              <i className={`fas ${
                alert.type === 'success' ? 'fa-check-circle' :
                alert.type === 'error' ? 'fa-exclamation-circle' :
                'fa-info-circle'
              }`}></i>
              <span>{alert.message}</span>
            </div>
          )}
          
          <form onSubmit={handleResetPassword}>
            <h2 className="card-title">Set New Password</h2>
            <p className="card-subtitle">Enter and confirm your new password</p>
            
            <div className="form-group">
              <label htmlFor="newPassword" className="form-label">
                <i className="fas fa-lock"></i> New Password
              </label>
              <input
                type="password"
                id="newPassword"
                className="form-input"
                placeholder="Enter new password (min 6 chars, no spaces)"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                onKeyPress={handleKeyPress}
                required
                minLength="6"
                disabled={isLoading}
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="confirmPassword" className="form-label">
                <i className="fas fa-lock"></i> Confirm Password
              </label>
              <input
                type="password"
                id="confirmPassword"
                className="form-input"
                placeholder="Confirm your password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                onKeyPress={handleKeyPress}
                required
                minLength="6"
                disabled={isLoading}
              />
            </div>
            
            <button 
              type="submit"
              className="btn btn-primary" 
              disabled={isLoading || !sessionReady}
            >
              {isLoading ? (
                <>
                  <div className="loading"></div> Resetting...
                </>
              ) : (
                <>
                  <i className="fas fa-check"></i> Reset Password
                </>
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

export default ResetPassword;
